import type { Device, DisplayCommand } from "@gooner/shared";
import { api } from "../../lib/api";

/**
 * Point a screen at a view — the whole of what "assignment" means, in one place.
 *
 * Two halves, and neither is optional:
 *
 * 1. **The write.** `POST /devices/:id/showing` is what makes the assignment true. It is
 *    what a reload reads, what the rail redraws from, and what a screen joining later
 *    resolves. Without it the screen changes and the change is a lie.
 *
 * 2. **The nudge.** A screen already holding a display socket is joined to the room of
 *    the view it was showing, and the server has no message that moves a socket between
 *    rooms — so the write alone leaves that screen playing the OLD view until something
 *    makes it re-resolve. `showing` is that something: it carries the new view id, and the
 *    screen re-points itself and joins the new room.
 *
 *    It used to be `reload`, which worked and cost too much. A reload always leaves
 *    fullscreen and re-entering it needs a fresh user gesture, so every retarget knocked a
 *    fullscreen television out of fullscreen — a page-level hammer swung to change one
 *    string. `reload` is still on the wire for a genuine restart; pointing a screen
 *    somewhere is not one.
 *
 * The order matters and is not arbitrary. The write goes first and is awaited, so the
 * reloading screen cannot possibly read the assignment it is being reloaded because of
 * before that assignment exists. Nudging first is a race that resolves correctly most of
 * the time on a fast link, which is the worst kind.
 *
 * The nudge is fire-and-forget on purpose: a screen that is not connected has nothing to
 * reload, and will read the new assignment when it next resolves. So a failed send is not
 * a failed assignment, and must not be reported as one.
 *
 * Both gestures land here — dragging a view card onto a device card, and choosing from
 * that card's `Showing:` select. They are the same capability, not a feature and its
 * fallback, which is why there is one function rather than a path each.
 */
export async function assignView(
  deviceId: string,
  viewId: string | null,
  nudge: (deviceId: string, cmd: DisplayCommand) => void,
): Promise<Device> {
  const { device } = await api.devices.setShowing(deviceId, viewId);
  try {
    // Only a real assignment can be nudged. `viewId: null` means "clear it", which the
    // server answers by handing the screen a fresh quick setup — there is no id to send,
    // so that screen learns what it got on its next resolve.
    if (viewId) nudge(deviceId, { kind: "showing", viewId });
  } catch {
    // Swallowed deliberately, and this is the line the rule above lives on. `sendCommand`
    // reaches through a socket that may be down or reconnecting, and a throw from it is
    // not a failed assignment — the write already succeeded, and the screen will read it
    // the next time it resolves. Letting it propagate would report a completed assignment
    // as an error and invite the operator to redo work that was already done.
  }
  return device;
}
